import {
  filter,
  includes,
  propEq,
  pathOr,
  map,
  flatten,
  indexOf,
  equals,
  reduce,
  head,
  nth,
  remove,
} from 'ramda';

import { getNamespaceId } from 'src/services/objects/namespaces';
import { traverseBF } from 'src/utilities/tree';
import { starBankLoanStages } from 'src/features/core/ChatEngine/Configs/lonStagesConfig/starbank';
import { filterIndexed } from 'src/utilities/visualFieldsMapper';
import {
  filterIsNotEmpty,
  isEmptyOrNil,
  isNotEmpty,
} from 'src/utilities/hasValue';
import { StageTreeBuilder } from './stageTree';
import { rsntLoanStages } from 'src/features/core/ChatEngine/Configs/lonStagesConfig/rsnt/rsnt';
import { rsntHelocLoanStages } from 'src/features/core/ChatEngine/Configs/lonStagesConfig/rsnt/heloc';
import { firstCBTLoanStages } from './firstcbt/firstcbt';
import { attuneLoanStages } from './app/attune';
import { breakoutLoanStages } from './breakout/breakout';
import { suttonBankLoanStages } from './suttonBank';
import { csbnetLoanStages } from './csbnet/csbnet';
import { midwestBankLoanStages } from './midwest/midwestbank';
import { isArray } from 'src/utilities/types';
import { texasBankApplicationStages } from './texassecurity';

// exported for testing only
export const stageConfigs = flatten([
  firstCBTLoanStages,
  rsntLoanStages,
  rsntHelocLoanStages,
  attuneLoanStages,
  breakoutLoanStages,
  suttonBankLoanStages,
  csbnetLoanStages,
  midwestBankLoanStages,
  starBankLoanStages,
  texasBankApplicationStages,
]);

const getStageConfig = (stageConfig: any, permissions: any, loanApp?:any) => {
  return new StageTreeBuilder(stageConfig, permissions, loanApp).getRootNode();
};

// getLoanStageConfigs is used to convert the json config into Node btree
// exported for testing only
export const getLoanStageConfigs = (stages: any, permissions?: any) => {
  return map((item: any) => {
    return {
      ...item,
      stages: getStageConfig(item, permissions),
    };
  }, stages);
};

const filterLoanStagesWithoutLoanType = (
  namespaceId: string,
  role: any,
  permissions?: any,
) => {
  const result = filter(
    (config: any) => {
      const rolesMatch = isArray(role)
        ? isNotEmpty(
            filter(
              (item) => includes(item, role),
              pathOr([], ['roles'], config),
            ),
          )
        : includes(role, pathOr([], ['roles'], config));
      return propEq('namespace_id', namespaceId)(config) && rolesMatch;
    },
    getLoanStageConfigs(stageConfigs, permissions),
  );
  return result;
};

const filterLoanStage = (data: any) => {
  return flatten(
    map((x: any) => {
      return traverseBF(x);
    }, data),
  );
};

type Stage = {
  name: string;
  display_name: string;
  order?: number;
};

type Child = {
  permission?: string;
  stages: Stage[];
};

type StageData = {
  name: string;
  display_name: string;
  order_id: number;
  children: Child[];
};

// Function to merge stages, ensuring uniqueness by 'name'
const mergeStages = (stages1: Stage[], stages2: Stage[]): Stage[] => {
  const mergedStages = [...stages1, ...stages2];
  const stageMap = new Map<string, Stage>();
  mergedStages.forEach((stage) => {
    stageMap.set(stage.name, stage);
  });
  return Array.from(stageMap.values());
};

// Function to merge children by 'permission'
const mergeChildren = (children1: Child[], children2: Child[]): Child[] => {
  const mergedChildren = [...children1];

  children2.forEach((child2) => {
    // Find a matching child by 'permission'
    const matchingChild = mergedChildren.find(
      (child1) => child1.permission === child2.permission,
    );

    if (matchingChild) {
      // Merge stages if permission matches
      matchingChild.stages = mergeStages(matchingChild.stages, child2.stages);
    } else {
      // Add the child if no match is found
      mergedChildren.push(child2);
    }
  });

  return mergedChildren;
};

// Function to merge the main stage data
const mergeStageData = (
  stageData1: StageData[],
  stageData2: StageData[],
): StageData[] => {
  const finalStages: StageData[] = [];
  stageData2 = filterIsNotEmpty(stageData2);
  filterIsNotEmpty(stageData1).forEach((stage: StageData) => {
    const matchIndex = stageData2.findIndex(
      (stage2) => stage2.name === stage.name,
    );
    if (matchIndex < 0) {
      finalStages.push(stage);
    }

    const matchingStage = nth(matchIndex, stageData2);
    matchingStage &&
      finalStages.push({
        ...matchingStage,
        children: mergeChildren(
          matchingStage.children || [],
          stage.children || [],
        ),
      });
    stageData2 = remove(matchIndex, 1, stageData2);
  });

  return flatten([finalStages, stageData2]);
};

export const filterLoanStages = (
  tenantId: string,
  loanType: string,
  roles: string[],
  subProductType?: string,
  permissions?: any,
  loanApp?: any,
) => {
  const stages = filter((config: any) => {
    const rolesMatch = isArray(roles)
      ? isNotEmpty(
          filter(
            (item) => includes(item, roles),
            pathOr([], ['roles'], config),
          ),
        )
      : includes(roles, pathOr([], ['roles'], config));

    const hasProductMatch =
      isEmptyOrNil(subProductType) ||
      includes(subProductType, pathOr([], ['sub_loan_types'], config));

    return (
      propEq('namespace_id', tenantId)(config) &&
      includes(loanType, pathOr([], ['loan_type'], config)) &&
      rolesMatch &&
      hasProductMatch
    );
  }, stageConfigs);

  const config = head(stages);

  const mergedLoanStages = reduce(
    mergeStageData,
    [],
    map(pathOr([], ['stages']), stages),
  );

  return (
    config && {
      ...config,
      stages: getStageConfig(
        {
          ...config,
          stages: mergedLoanStages,
        },
        permissions,
        loanApp
      ),
    }
  );
};

export const filterLoanStagesWithNameSpaceId = (
  role: any,
  permissions: any,
) => {
  const data = map(
    (config: any) => {
      return pathOr('', ['stages'], config);
    },
    filterLoanStagesWithoutLoanType(getNamespaceId(), role, permissions),
  );

  const loanStages = [...filterLoanStage(data)];

  const filteredLoanStages = filterIndexed(
    (status: any, index: number) => equals(indexOf(status, loanStages), index),
    loanStages,
  );

  return filteredLoanStages;
};
